import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { getAdminAllProjectsKey, rotateAdminAllProjectsKey } from '../../api/mcp-keys'
import { useDialog } from '../../components/dialogs/useDialog'
import type { AdminAllProjectsMcpKey } from '../../types/api'

export default function AdminApiKeyPage() {
  const dialog = useDialog()
  const [adminKey, setAdminKey] = useState<AdminAllProjectsMcpKey | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRotating, setIsRotating] = useState(false)

  const loadKey = useCallback(async () => {
    try {
      const data = await getAdminAllProjectsKey()
      setAdminKey(data)
    } catch {
      toast.error('Failed to load admin API key')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadKey()
  }, [loadKey])

  const copyKey = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token)
      toast.success('Admin MCP key copied')
    } catch {
      toast.error('Failed to copy admin MCP key')
    }
  }

  const handleRotate = async () => {
    const isRotate = Boolean(adminKey?.mcpKeyPrefix)
    if (isRotate) {
      const confirmed = await dialog.confirm({
        title: 'Rotate admin all-projects key?',
        description: 'The previous key will stop working immediately.',
        confirmLabel: 'Rotate key',
        tone: 'danger',
      })
      if (!confirmed) return
    }

    setIsRotating(true)
    try {
      const rotated = await rotateAdminAllProjectsKey()
      setAdminKey((prev) =>
        prev
          ? {
              ...prev,
              keyId: rotated.id,
              mcpKeyPrefix: rotated.tokenPrefix,
              mcpKeyToken: rotated.token,
              mcpKeyLastUsedAt: null,
            }
          : {
              keyId: rotated.id,
              name: 'admin-all-projects',
              mcpKeyPrefix: rotated.tokenPrefix,
              mcpKeyToken: rotated.token,
              mcpKeyLastUsedAt: null,
              createdAt: new Date().toISOString(),
            }
      )
      toast.success(isRotate ? 'Admin API key rotated' : 'Admin API key generated')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to rotate admin API key')
    } finally {
      setIsRotating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-harbor-text">Admin API Key</h1>
        <p className="text-harbor-text-secondary mt-1">
          All-projects MCP key with admin-level access across every project. Use in Cursor or other
          MCP clients when you need unrestricted tool access.
        </p>
      </div>

      <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-harbor-text">Current key</h2>
          <p className="text-sm text-harbor-text-secondary mt-1">
            Full key is stored encrypted for admin retrieval. Rotate to invalidate the previous
            secret.
          </p>
        </div>

        {adminKey?.mcpKeyToken ? (
          <div className="space-y-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-harbor-text-muted">Name</dt>
                <dd className="text-harbor-text font-medium">
                  {adminKey.name ?? 'admin-all-projects'}
                </dd>
              </div>
              <div>
                <dt className="text-harbor-text-muted">Created</dt>
                <dd className="text-harbor-text">
                  {adminKey.createdAt ? new Date(adminKey.createdAt).toLocaleString() : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-harbor-text-muted">Last used</dt>
                <dd className="text-harbor-text">
                  {adminKey.mcpKeyLastUsedAt
                    ? new Date(adminKey.mcpKeyLastUsedAt).toLocaleString()
                    : 'Never'}
                </dd>
              </div>
            </dl>
            <div>
              <p className="text-xs font-medium text-harbor-text-muted uppercase tracking-wider mb-2">
                MCP key
              </p>
              <code className="block text-xs font-mono text-emerald-100 bg-harbor-bg border border-harbor-border rounded-lg p-3 break-all">
                {adminKey.mcpKeyToken}
              </code>
              <button
                type="button"
                onClick={() => void copyKey(adminKey.mcpKeyToken!)}
                className="mt-2 px-3 py-1.5 text-xs text-harbor-text-secondary border border-harbor-border rounded-lg hover:bg-harbor-surface-raised transition-colors"
              >
                Copy key
              </button>
            </div>
          </div>
        ) : adminKey?.mcpKeyPrefix ? (
          <div className="space-y-2">
            <p className="text-sm font-mono text-harbor-text-secondary">{adminKey.mcpKeyPrefix}…</p>
            <p className="text-sm text-amber-200/90">
              Rotate once to store a retrievable full admin key.
            </p>
          </div>
        ) : (
          <p className="text-sm text-harbor-text-muted">Provisioning admin key…</p>
        )}

        <button
          type="button"
          onClick={() => void handleRotate()}
          disabled={isRotating}
          className="px-5 py-2.5 bg-emerald-600 text-harbor-text font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          {isRotating ? 'Working...' : adminKey?.mcpKeyPrefix ? 'Rotate key' : 'Generate key'}
        </button>
      </div>
    </div>
  )
}
